/**
 * Utility for payment method formatting and Pokok calculation
 * Sesuai instruksi SDMT Ponorogo:
 * 1. Hilangkan kata DUITKU di front end, tampilkan nama sistem pembayaran resmi (QRIS, VA BRI, VA BNI, VA BCA, dll.)
 * 2. Total bayar keuangan sekolah hanya mencatat POKOK (fee aplikasi / biaya layanan tidak masuk database keuangan sekolah)
 */

const BANK_NAMES: Record<string, string> = {
  BR: 'VA BRI',
  BC: 'VA BCA',
  M2: 'VA Mandiri',
  I1: 'VA BNI',
  BN: 'VA BNI',
  BT: 'VA Permata',
  B1: 'VA CIMB Niaga',
  BV: 'VA BSI',
  AG: 'VA Artha Graha',
  S1: 'VA Sahabat Sampoerna',
  A1: 'VA ATM Bersama',
  NC: 'VA BNC',
  VA: 'Virtual Account',
  SP: 'QRIS',
  NQ: 'QRIS',
  LQ: 'QRIS',
  GQ: 'QRIS',
  OV: 'OVO',
  DA: 'DANA',
  LA: 'LinkAja',
  SA: 'ShopeePay',
  IR: 'Indomaret',
  FT: 'Retail',
};

/**
 * Format channel/metode_bayar by removing DUITKU references and returning clean payment method name
 * Examples:
 * - DUITKU_QRIS -> QRIS
 * - DUITKU_BR -> VA BRI
 * - DUITKU_VA -> Virtual Account
 * - DUITKU_BC -> VA BCA
 * - DUITKU_M2 -> VA Mandiri
 * - DUITKU_I1 -> VA BNI
 * - CASH / MANUAL_CASH -> CASH
 */
export function formatPaymentMethod(channel?: string | null, metodeBayar?: string | null): string {
  const candidates = [metodeBayar, channel].filter(Boolean) as string[];
  if (candidates.length === 0) return 'CASH';

  for (const raw of candidates) {
    const val = raw.trim();
    const upper = val.toUpperCase();

    // Check QRIS
    if (upper.includes('QRIS')) {
      return 'QRIS';
    }

    // Direct match with prefix DUITKU_
    if (upper.startsWith('DUITKU_')) {
      const code = upper.replace(/^DUITKU_/, '').trim();
      if (code === 'QRIS' || code === 'SP' || code === 'NQ' || code === 'LQ' || code === 'GQ') {
        return 'QRIS';
      }
      if (BANK_NAMES[code]) {
        return BANK_NAMES[code];
      }
      if (code === 'VA') {
        // If candidate 1 was DUITKU_VA, maybe candidate 2 has specific bank (e.g. DUITKU_BR)
        continue;
      }
      return code.length <= 3 ? `VA ${code}` : code;
    }

    // Direct match with prefix VA_
    if (upper.startsWith('VA_')) {
      const code = upper.replace(/^VA_/, '').trim();
      if (BANK_NAMES[code]) {
        return BANK_NAMES[code];
      }
      return `VA ${code}`;
    }

    // Direct code lookup
    if (BANK_NAMES[upper]) {
      return BANK_NAMES[upper];
    }
  }

  // Fallback to the first candidate
  const first = candidates[0].trim();
  const upper = first.toUpperCase();

  if (upper.includes('QRIS')) return 'QRIS';
  if (upper === 'DUITKU_VA' || upper === 'VA') return 'Virtual Account';
  if (upper === 'MANUAL_CASH' || upper === 'CASH') return 'CASH';
  if (upper === 'TABUNGAN' || upper === 'SALDO_TABUNGAN') return 'Tabungan';
  if (upper === 'EWALLET' || upper === 'SALDO_EWALLET' || upper === 'E-WALLET') return 'E-Wallet';

  // Strip prefixes
  const cleaned = first.replace(/^(DUITKU_|MIDTRANS_|VA_)/i, '').trim();
  const upperCleaned = cleaned.toUpperCase();
  if (BANK_NAMES[upperCleaned]) {
    return BANK_NAMES[upperCleaned];
  }
  if (upperCleaned.length <= 3) {
    return `VA ${upperCleaned}`;
  }
  return cleaned || 'CASH';
}

/**
 * Filter out fee items (FEE_APP, FEE_BANK, Biaya Layanan) so only school Pokok items remain
 */
export function filterPokokItems(items?: any[]): any[] {
  if (!Array.isArray(items)) return [];
  return items.filter((it: any) => {
    const id = (it.tagihanId || it.tagihan_id || '').toUpperCase();
    const pos = (it.posId || it.pos_id || '').toUpperCase();
    const name = (it.namaTagihan || it.nama_tagihan || '').toLowerCase();
    const posName = (it.posName || it.pos_name || '').toLowerCase();
    
    const isFee = id === 'FEE_APP' || id === 'FEE_BANK' ||
                  pos === 'FEE_APP' || pos === 'FEE_BANK' ||
                  name.includes('biaya layanan') || name.includes('biaya bank') ||
                  posName.includes('biaya layanan') || posName.includes('biaya bank');
    return !isFee;
  });
}

/**
 * Get pure Pokok payment amount (Keuangan Sekolah):
 * Excludes fee aplikasi / biaya layanan.
 */
export function getPokokNominal(trx: any): number {
  if (!trx) return 0;

  // 1. If transaction has items, sum non-fee items
  if (Array.isArray(trx.items) && trx.items.length > 0) {
    const pokokItems = filterPokokItems(trx.items);
    if (pokokItems.length > 0) {
      return pokokItems.reduce((sum: number, it: any) => sum + Number(it.nominal || 0), 0);
    }
  }

  // 2. Fallback on totalBayar / total_bayar
  let total = Number(trx.totalBayar ?? trx.total_bayar ?? 0);
  const fee = Number(trx.biayaLayanan ?? trx.biaya_layanan ?? 0);

  if (fee > 0 && total >= fee) {
    total -= fee;
  } else if (total > 1500 && total % 1000 === 500) {
    // Normalisasi biaya layanan 1.500 untuk semua transaksi gateway (QRIS maupun Virtual Account)
    total -= 1500;
  }

  return Math.max(0, total);
}
